import { parsePullRequestUrl } from "@afar-research/afar";
import type { EnvironmentId } from "@afar-research/contracts";
import { MessageSquareIcon } from "lucide-react";

import { pullRequestEnvironment } from "../../state/pullRequests";
import { useEnvironmentQuery } from "../../state/query";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

/**
 * Which of this Environment's threads hold the pull request in their collection. It is the
 * reverse of the thread panel, and the answer comes from the Environment's own projection: a
 * thread in another Environment linking the same repository and number is a different
 * membership and is never counted here.
 */
export function PullRequestThreadLinks({
  environmentId,
  url,
}: {
  readonly environmentId: EnvironmentId;
  /** The address the host returned, which is what makes the lookup host-qualified. */
  readonly url: string;
}) {
  const key = parsePullRequestUrl(url);
  const linked = useEnvironmentQuery(
    key === null ? null : pullRequestEnvironment.linkedThreads({ environmentId, input: key }),
  );
  const threads = linked.data?.threads;
  if (linked.error !== null && threads === undefined) {
    // A read that failed is not an answer. Saying nothing here would read as "no thread is
    // working on this", which is the one thing this surface must not claim wrongly.
    return (
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex shrink-0 items-center gap-1" />}>
          <MessageSquareIcon aria-hidden className="size-3.5" />
          <span aria-label="Could not read which threads link this pull request">?</span>
        </TooltipTrigger>
        <TooltipPopup side="bottom">{linked.error}</TooltipPopup>
      </Tooltip>
    );
  }
  // Until the read resolves the count is unknown, and a zero would claim it is none.
  if (threads === undefined || threads.length === 0) return null;
  const active = threads.filter((thread) => thread.archivedAt === null);
  const label =
    threads.length === 1
      ? `Linked to the thread ${threads[0]!.title}`
      : `Linked to ${threads.length} threads${
          active.length === threads.length ? "" : `, ${threads.length - active.length} archived`
        }`;
  return (
    <Tooltip>
      <TooltipTrigger
        render={<span className="inline-flex shrink-0 items-center gap-1 tabular-nums" />}
      >
        <MessageSquareIcon aria-hidden className="size-3.5" />
        <span aria-label={label}>{threads.length}</span>
      </TooltipTrigger>
      <TooltipPopup side="bottom">
        <span className="flex flex-col gap-0.5">
          {threads.map((thread) => (
            <span key={thread.id} className="truncate">
              {thread.title}
              {thread.archivedAt === null ? "" : " · archived"}
            </span>
          ))}
        </span>
      </TooltipPopup>
    </Tooltip>
  );
}
